import React, { useEffect, useState } from 'react';
import { Input, Popup } from '@root/components';
import { PRESET_STAKE_COUNT, VALID_GAME_ID } from '@root/constants';
import { useAuth } from '@root/hooks';
import { appSelector, AppSliceState } from '@root/store/slices/appSlice';
import { betValuesSelector } from '@root/store/slices/betValuesSlice';
import {
  useActions,
  useLazyGetDefaultGamePreferencesQuery,
  useSaveGamePreferencesMutation,
  useTypedSelector,
} from '@root/store/store';
import { useAnalytics, useBetLimits } from '@shared/hooks';
import { AnalyticsEvent } from '@shared/services/analytics';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './CustomBetPopup.module.scss';

const ORDINAL_SUFFIXES = ['st', 'nd', 'rd', 'th', 'th', 'th', 'th'];

const getDuplicateValues = (vals: string[]): Set<string> => {
  const seen = new Map<string, number>();
  const duplicates = new Set<string>();
  vals.forEach((v) => {
    if (v && v !== '0') {
      const count = (seen.get(v) || 0) + 1;
      seen.set(v, count);
      if (count > 1) duplicates.add(v);
    }
  });
  return duplicates;
};

const CustomBetPopup: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { customBetPopupOpen } = useTypedSelector<AppSliceState>(appSelector);
  const { values, currencyCode } = useTypedSelector(betValuesSelector);
  const { setCustomBetPopupOpen, setBetValue, setBetValues } = useActions();
  const [saveGamePreferences] = useSaveGamePreferencesMutation();
  const [fetchDefaultPreferences] = useLazyGetDefaultGamePreferencesQuery();
  const { trackEvent } = useAnalytics();

  const { minBet: minAmount, maxBet: maxAmount } = useBetLimits();

  const [localValues, setLocalValues] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>(
    Array(PRESET_STAKE_COUNT).fill('')
  );

  useEffect(() => {
    setLocalValues(values.map((v: number) => v.toString()));
    setErrors(Array(PRESET_STAKE_COUNT).fill(''));
  }, [values, customBetPopupOpen]);

  useEffect(() => {
    if (customBetPopupOpen) {
      trackEvent(AnalyticsEvent.INSTANT_STAKE_EDIT_CLICKED);
    }
  }, [customBetPopupOpen, trackEvent]);

  const validateField = (vals: string[], index: number | null): boolean => {
    const newErrors = Array(PRESET_STAKE_COUNT).fill('');
    if (index === null) {
      setErrors(newErrors);
      return true;
    }
    const current = vals[index];
    const parsed = parseFloat(current);
    if (!current) {
      newErrors[index] = t('Field must not be empty');
      setErrors(newErrors);
      return false;
    }
    if (current === '0' || parsed <= 0) {
      newErrors[index] = t('Field value cannot be 0');
      setErrors(newErrors);
      return false;
    }
    if (parsed < minAmount || parsed > maxAmount) {
      newErrors[index] =
        `${t('Value must be greater than or equal to')} ${minAmount.toFixed(2)} ${t('and less than or equal to')} ${maxAmount.toFixed(2)}`;
      setErrors(newErrors);
      return false;
    }
    if (getDuplicateValues(vals).has(current)) {
      newErrors[index] = t('Duplicate Value! Enter Again');
    }
    setErrors(newErrors);
    return newErrors.every((err) => !err);
  };

  const handleInputChange = (index: number, value: string) => {
    if (value !== '' && !/^\d{0,7}\.?\d{0,2}$/.test(value)) return;
    const newValues = [...localValues];
    newValues[index] = value;
    setLocalValues(newValues);
    validateField(newValues, index);
  };

  const handleInputFocus = (index: number) => {
    validateField(localValues, index);
  };

  const isFormValid =
    localValues.every((v) => v && v !== '0') &&
    errors.every((err) => !err) &&
    getDuplicateValues(localValues).size === 0 &&
    localValues.every((v) => {
      const num = parseFloat(v);
      if (isNaN(num)) return false;
      if (num < minAmount) return false;
      if (num > maxAmount) return false;
      return true;
    });

  const handleReset = async () => {
    trackEvent(AnalyticsEvent.INSTANT_STAKE_RESET_CLICKED);
    if (!user) return;
    // Back to the backend's default stakes. If the call fails, nothing changes.
    try {
      const response = await fetchDefaultPreferences({
        userId: user.id,
        gameId: VALID_GAME_ID,
      }).unwrap();
      const stakes = response.data?.stakes?.slice(0, PRESET_STAKE_COUNT);
      if (stakes && stakes.length > 0) setBetValues(stakes);
    } catch (error) {
      console.error('[GamePreferences] Default stakes error:', error);
    }
  };

  const handleApply = async () => {
    trackEvent(AnalyticsEvent.INSTANT_STAKE_APPLY_CLICKED);
    const stakes = localValues
      .map((v) => parseFloat(v))
      .filter((v) => !isNaN(v) && v > 0)
      .sort((a, b) => a - b);

    if (user && stakes.length > 0) {
      try {
        await saveGamePreferences({
          userId: user.id,
          gameId: VALID_GAME_ID,
          stakes,
        }).unwrap();
        stakes.forEach((val, index) => setBetValue({ index, value: val }));
      } catch {
        // save failed server-side; popup still closes without applying new values
      }
    }
    setCustomBetPopupOpen(false);
  };

  return (
    <Popup
      open={customBetPopupOpen}
      onClose={() => {
        trackEvent(AnalyticsEvent.INSTANT_STAKE_CANCEL_CLICKED);
        setCustomBetPopupOpen(false);
      }}
      title={t('Custom Bet Values')}
      titleClassName={styles.popupTitle}
      maxHeight="90vh"
    >
      <div className={styles.contentContainer}>
        <p className={styles.subtitle}>
          {t('Min. Amount')} {minAmount.toFixed(2)}
        </p>
        <div className={styles.inputsContainer}>
          {localValues.map((value, index) => (
            <Input
              key={index}
              placeholder={t(
                `Enter ${index + 1}${ORDINAL_SUFFIXES[index]} Value`
              )}
              value={value}
              prefix={currencyCode}
              onChange={(v) => handleInputChange(index, v)}
              onFocus={() => handleInputFocus(index)}
              error={errors[index]}
              className={cn({
                [styles.inputItem_wide]:
                  localValues.length % 2 === 1 &&
                  index === localValues.length - 1,
              })}
            />
          ))}
        </div>
        <div className={styles.buttonsContainer}>
          <div className={styles.buttonsContainer__left}>
            <button className={styles.resetButton} onClick={handleReset}>
              <span>{t('Reset')}</span>
            </button>
          </div>
          <div className={styles.buttonsContainer__right}>
            <button
              className={styles.button}
              onClick={handleApply}
              disabled={!isFormValid}
            >
              <span>{t('Apply')}</span>
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default CustomBetPopup;
